<script>
  import { onMount } from 'svelte';
  import SettingsLayout from '../../components/SettingsLayout.svelte';
  import { navigate } from 'svelte-routing';
  import { settingsStore } from '../../stores/settingsStore.js';
  import { COUNTRY_LABELS } from '../../lib/settings/constants.js';
  import { provinceOptions, countryValue, PROVINCES, provinceTaxComponents, provinceHasPst, xeroPurchaseRateHint } from '../../lib/canadaTax.js';
  import {
    filterSalesAccounts,
    filterExpenseAccounts,
    formatAccountOptionLabel,
    loadXeroChartAccounts
  } from '../../lib/settings/xeroAccountFilters.js';

  const {
    formData,
    billSettings,
    xeroTaxRates,
    loadingTaxRates,
    xeroConnected,
    xeroNeedsFix,
    checkingXeroStatus,
    showDisconnectConfirm,
    disconnecting,
    managedByOwner,
    loading,
    handleCountryChange,
    disconnectXero,
    saveSettingsForNavigation
  } = settingsStore;

  let navigatingToMappings = false;

  // Canada is chosen per province; the picker value is 'CA-BC' etc.
  const caProvinceOptions = provinceOptions();
  $: pickerValue = countryValue($formData.country, $formData.province);
  $: caProvince = $formData.country === 'CA' ? $formData.province : null;
  $: caComponents = provinceTaxComponents(caProvince);
  let xeroAccounts = [];
  let loadingAccounts = false;
  let refreshingAccounts = false;
  let accountsError = null;
  let accountsLoadAttempted = false;
  let xeroOrganisationName = null;
  let tenantInfoLoaded = false;
  let identityChangedNotice = false;

  onMount(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('setup') === 'required') {
      window.history.replaceState({}, '', '/settings/xero');
    }
    if (params.get('identity_changed') === '1') {
      identityChangedNotice = true;
      window.history.replaceState({}, '', '/settings/xero');
    }
  });

  $: missingDefaultCodes =
    !($formData.xeroInvoiceCode || '').trim() || !($formData.xeroExpenseCode || '').trim();

  $: activeOrgName = xeroOrganisationName || null;

  $: salesAccounts = filterSalesAccounts(xeroAccounts);
  $: expenseAccounts = filterExpenseAccounts(xeroAccounts);

  $: salesCodeInList = salesAccounts.some(
    (a) => String(a.code) === String(($formData.xeroInvoiceCode || '').trim())
  );
  $: expenseCodeInList = expenseAccounts.some(
    (a) => String(a.code) === String(($formData.xeroExpenseCode || '').trim())
  );

  // Invoice tax dropdowns only list revenue-applicable rates. Surface a warning
  // when a previously saved purchase-side type (e.g. INPUT) is no longer listed.
  $: selectedInvoiceTaxType =
    $formData.country === 'US'
      ? $formData.xeroUSTaxType
      : $formData.country === 'CA'
        ? $formData.xeroCATaxType
        : $formData.country === 'GB' || $formData.country === 'GI'
          ? $formData.xeroGBTaxType
          : '';
  $: invoiceTaxTypeInList =
    !selectedInvoiceTaxType ||
    $xeroTaxRates.some((r) => r.taxType === selectedInvoiceTaxType);

  // Load CoA when Xero becomes connected (once per connection).
  $: if ($xeroConnected && !accountsLoadAttempted && !loadingAccounts) {
    loadAccounts();
  }
  $: if (!$xeroConnected) {
    accountsLoadAttempted = false;
    xeroAccounts = [];
    accountsError = null;
  }

  // Load the connected Xero org name / tenant list once per connection.
  $: if ($xeroConnected && !tenantInfoLoaded) {
    loadXeroTenantInfo();
  }
  $: if (!$xeroConnected) {
    tenantInfoLoaded = false;
    xeroOrganisationName = null;
  }

  async function loadXeroTenantInfo() {
    tenantInfoLoaded = true;
    try {
      const res = await fetch('/api/xero/check-auth');
      const data = await res.json();
      if (res.ok && data.authenticated) {
        xeroOrganisationName = data.xeroOrganisationName || null;
      }
    } catch (err) {
      console.error('Failed to load Xero tenant info:', err);
    }
  }

  async function loadAccounts({ forceRefresh = false } = {}) {
    loadingAccounts = true;
    accountsError = null;
    accountsLoadAttempted = true;
    try {
      const { accounts } = await loadXeroChartAccounts({ forceRefresh });
      xeroAccounts = accounts;
    } catch (err) {
      console.error('Failed to load Xero chart accounts:', err);
      accountsError = err.message || 'Failed to load Xero chart of accounts';
      xeroAccounts = [];
    } finally {
      loadingAccounts = false;
    }
  }

  async function refreshAccounts() {
    refreshingAccounts = true;
    try {
      await loadAccounts({ forceRefresh: true });
    } finally {
      refreshingAccounts = false;
    }
  }

  async function goToMappings(event) {
    event.preventDefault();
    if (navigatingToMappings || $loading) return;

    // Read-only members have nothing to save on this screen.
    if ($managedByOwner) {
      navigate('/settings/xero-mapping');
      return;
    }

    navigatingToMappings = true;
    try {
      const saved = await saveSettingsForNavigation();
      if (saved) {
        navigate('/settings/xero-mapping');
      }
    } finally {
      navigatingToMappings = false;
    }
  }
</script>

<SettingsLayout activeTab="xero" tabTitle="Xero Settings">
  <div class="space-y-6">
    <!-- Connection status + connect/disconnect stay interactive for everyone:
         each user (including members) authenticates to Xero with their OWN login.
         Only the Xero settings below are managed by the org owner for members. -->
    <div class="flex items-center gap-3 mb-2">
      {#if $checkingXeroStatus}
        <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300">
          Checking…
        </span>
      {:else if $xeroConnected}
        <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-300">
          <span class="w-1.5 h-1.5 mr-1.5 rounded-full bg-green-500"></span>
          Connected
        </span>
      {:else if $xeroNeedsFix}
        <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-900 border border-amber-300">
          <span class="w-1.5 h-1.5 mr-1.5 rounded-full bg-amber-500"></span>
          Needs fixing
        </span>
      {:else}
        <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-300">
          <span class="w-1.5 h-1.5 mr-1.5 rounded-full bg-red-500"></span>
          Not Connected
        </span>
      {/if}

      {#if $xeroConnected && xeroOrganisationName}
        <span class="text-xs text-gray-600">
          <span class="font-medium text-gray-500">Xero Account:</span>
          {xeroOrganisationName}
        </span>
      {/if}
    </div>

    {#if identityChangedNotice}
      <div class="mb-4 p-3 bg-blue-50 border border-blue-200 text-blue-800 rounded text-sm">
        Xero connection updated.
      </div>
    {/if}

    {#if !$checkingXeroStatus && !$xeroConnected && !$xeroNeedsFix}
      <div class="pb-4">
        <a
          href="/api/xero/login"
          class="inline-flex items-center px-4 py-2 text-sm font-medium !text-white hover:!text-white visited:!text-white no-underline hover:no-underline bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          Connect to Xero
        </a>
        <p class="mt-2 text-sm text-gray-600">
          Connect first, then choose your default sales and expense codes from the dropdowns.
        </p>
      </div>
    {:else if $xeroConnected || $xeroNeedsFix}
      <div class="pb-4">
        {#if $xeroNeedsFix && !$xeroConnected}
          <div class="mb-3 p-3 bg-amber-50 border border-amber-200 text-amber-900 rounded text-sm">
            Xero needs fixing. Disconnect to clear the pinned organisation, then connect again.
          </div>
        {/if}
        {#if !$showDisconnectConfirm}
          <button
            type="button"
            on:click={() => showDisconnectConfirm.set(true)}
            class="inline-flex items-center px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-300 rounded-md hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            {activeOrgName ? `Disconnect ${activeOrgName}` : 'Disconnect from Xero'}
          </button>
          {#if $xeroConnected}
            <p class="mt-2">
              <a
                href="/api/xero/login?prompt=login"
                class="text-xs text-gray-500 hover:text-gray-700 underline"
              >Reconnect as a different Xero user</a>
            </p>
          {/if}
        {:else}
          <div class="p-4 bg-red-50 border border-red-300 rounded-lg">
            <div class="flex items-start mb-3">
              <svg class="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p class="text-sm font-semibold text-red-800">
                  Are you sure you want to disconnect{activeOrgName ? ` ${activeOrgName}` : ' from Xero'}?
                </p>
                <p class="text-sm text-red-700 mt-1">This will:</p>
                <ul class="text-sm text-red-700 list-disc list-inside mt-1 space-y-0.5">
                  <li>Revoke Buildzilla's access to your Xero organisation</li>
                  <li>Stop webhook notifications for that organisation</li>
                  <li>Clear your Xero account codes and integration settings</li>
                </ul>
                <p class="text-sm text-red-700 mt-2">You can reconnect at any time from the dashboard.</p>
              </div>
            </div>
            <div class="flex gap-3">
              <button
                type="button"
                on:click={disconnectXero}
                disabled={$disconnecting}
                class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                {#if $disconnecting}
                  <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Disconnecting...
                {:else}
                  Yes, Disconnect
                {/if}
              </button>
              <button
                type="button"
                on:click={() => showDisconnectConfirm.set(false)}
                disabled={$disconnecting}
                class="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-[#f0f4ff] disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Cancel
              </button>
            </div>
          </div>
        {/if}
      </div>
    {/if}

    <fieldset class="contents" disabled={$managedByOwner}>
    {#if $xeroConnected && missingDefaultCodes}
      <div class="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
        Xero is linked, but setup is incomplete. Choose and save your Default Sales and Expense codes below before syncing.
      </div>
    {/if}

    <div>
      <div class="flex items-center justify-between gap-3 mb-2">
        <h4 class="text-base font-semibold text-gray-900">Default Xero Chart of Accounts Codes</h4>
        {#if $xeroConnected}
          <button
            type="button"
            on:click={refreshAccounts}
            disabled={refreshingAccounts || loadingAccounts || $managedByOwner}
            class="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {#if refreshingAccounts || loadingAccounts}
              <div class="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-blue-600 mr-1.5"></div>
              Loading…
            {:else}
              Refresh accounts
            {/if}
          </button>
        {/if}
      </div>
      <div class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
        <p class="text-sm text-blue-800 mb-2">
          📖 <strong>Setup Xero Account Codes:</strong>
        </p>
        <ol class="text-sm text-blue-800 list-decimal list-inside space-y-1">
          <li>These codes are used for catch-all defaults when syncing to Xero.</li>
          {#if $xeroConnected}
            <li>Select from your Xero Chart of Accounts below (sales/revenue and expense accounts only).</li>
          {:else}
            <li>Connect to Xero to choose codes from a dropdown, or enter them manually for now. Find them in Xero under Accounting → Chart of Accounts. <a href="https://xero.com" target="_blank" class="text-blue-600 underline hover:text-blue-800">xero.com</a></li>
          {/if}
        </ol>
      </div>
      {#if accountsError}
        <div class="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p class="text-sm text-yellow-800">{accountsError}. You can enter codes manually below.</p>
        </div>
      {/if}
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label for="xeroInvoiceCode" class="block text-sm font-medium text-gray-700 mb-2">
            Default Sales Code *
          </label>
          {#if $xeroConnected && xeroAccounts.length > 0}
            <select
              id="xeroInvoiceCode"
              bind:value={$formData.xeroInvoiceCode}
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              required
            >
              <option value="">Select a code</option>
              {#if ($formData.xeroInvoiceCode || '').trim() && !salesCodeInList}
                <option value={$formData.xeroInvoiceCode}>
                  {$formData.xeroInvoiceCode} (not in sales accounts — please reselect)
                </option>
              {/if}
              {#each salesAccounts as account}
                <option value={account.code}>{formatAccountOptionLabel(account)}</option>
              {/each}
            </select>
          {:else if $xeroConnected && loadingAccounts}
            <div class="flex items-center px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-sm text-gray-600">
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
              Loading accounts…
            </div>
          {:else}
            <input
              type="text"
              id="xeroInvoiceCode"
              bind:value={$formData.xeroInvoiceCode}
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="e.g., 200"
              required
            />
          {/if}
        </div>
        <div>
          <label for="xeroExpenseCode" class="block text-sm font-medium text-gray-700 mb-2">
            Default Expenses/Purchases Code *
          </label>
          {#if $xeroConnected && xeroAccounts.length > 0}
            <select
              id="xeroExpenseCode"
              bind:value={$formData.xeroExpenseCode}
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              required
            >
              <option value="">Select a code</option>
              {#if ($formData.xeroExpenseCode || '').trim() && !expenseCodeInList}
                <option value={$formData.xeroExpenseCode}>
                  {$formData.xeroExpenseCode} (not in expense accounts — please reselect)
                </option>
              {/if}
              {#each expenseAccounts as account}
                <option value={account.code}>{formatAccountOptionLabel(account)}</option>
              {/each}
            </select>
          {:else if $xeroConnected && loadingAccounts}
            <div class="flex items-center px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-sm text-gray-600">
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
              Loading accounts…
            </div>
          {:else}
            <input
              type="text"
              id="xeroExpenseCode"
              bind:value={$formData.xeroExpenseCode}
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="e.g., 400"
              required
            />
          {/if}
        </div>
      </div>
    </div>

    <div class="border-t border-gray-200 pt-4">
      <h4 class="text-base font-semibold text-gray-900 mb-2">Advanced Xero Settings</h4>
      <a
        href="/settings/xero-mapping"
        on:click={goToMappings}
        class="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium
          {navigatingToMappings || $loading ? 'opacity-60 pointer-events-none' : ''}"
        aria-busy={navigatingToMappings || $loading}
      >
        <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
        {navigatingToMappings ? 'Saving settings…' : 'Manage JobTread → Xero Cost Code Mappings'}
      </a>
      <p class="text-sm text-gray-600 mt-1">
        Map your JobTread parent cost codes to Xero accounts for bills (expense) and customer invoices (sales) separately.
        Unsaved Xero settings are saved before opening this page.
      </p>
    </div>

    <div class="border-t border-gray-200 pt-4">
      <h4 class="text-base font-semibold text-gray-900 mb-2">Country</h4>
      <label for="country" class="block text-sm font-medium text-gray-700 mb-2">
        Country * <span class="font-normal text-gray-500">({caProvince ? `Canada – ${PROVINCES[caProvince].name}` : (COUNTRY_LABELS[$formData.country] || $formData.country)})</span>
      </label>
      <select
        id="country"
        value={pickerValue}
        on:change={handleCountryChange}
        class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
        required
      >
        <option value="AU">Australia</option>
        {#if $formData.country === 'CA' && !caProvince}
          <option value="CA">Canada (choose your province below)</option>
        {/if}
        <optgroup label="Canada">
          {#each caProvinceOptions as p}
            <option value={p.value}>{p.label}</option>
          {/each}
        </optgroup>
        <option value="GB">United Kingdom (GBP)</option>
        <option value="GI">Gibraltar (GIP)</option>
        <option value="NZ">New Zealand</option>
        <option value="US">United States</option>
      </select>
    </div>

    {#if $formData.country === 'US'}
      <div class="border-t pt-6">
        <h4 class="text-lg font-medium text-gray-900 mb-4">US Sales Tax Configuration</h4>
        <div class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p class="text-sm text-blue-800">
            📖 <strong>About US Sales Tax:</strong><br>Select the Xero tax setting that matches your sales tax jurisdiction best. <br>
            This tax code will appear on the invoice created in Xero for invoices coming from JobTread that include tax components. <br>
            The tax calculated in JobTread will be the amount applied to the invoice in Xero. <br>
            <a href="/help/settings/configuring-xero-integration" target="_blank" class="text-blue-600 underline hover:text-blue-800">Learn more</a>
          </p>
        </div>
        {#if $loadingTaxRates}
          <div class="flex items-center justify-center py-4">
            <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 mr-2"></div>
            <span class="text-sm text-gray-600">Loading tax rates from Xero...</span>
          </div>
        {:else if $xeroTaxRates.length === 0}
          <div class="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <p class="text-sm text-yellow-800">
              ⚠️ No tax rates found in your Xero account. Please create a tax rate in Xero first, then refresh this page.
            </p>
          </div>
        {:else}
          <div>
            <label for="xeroUSTaxType" class="block text-sm font-medium text-gray-700 mb-2">US Sales Tax Rate</label>
            <select id="xeroUSTaxType" bind:value={$formData.xeroUSTaxType} class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500">
              <option value="">-- None (tax will not be applied) --</option>
              {#if selectedInvoiceTaxType && !invoiceTaxTypeInList}
                <option value={selectedInvoiceTaxType}>{selectedInvoiceTaxType} (not valid for sales — please reselect)</option>
              {/if}
              {#each $xeroTaxRates as rate}
                <option value={rate.taxType}>{rate.name} ({rate.displayRate}%)</option>
              {/each}
            </select>
            {#if selectedInvoiceTaxType && !invoiceTaxTypeInList}
              <p class="mt-2 text-sm text-amber-600">The saved tax rate cannot be used on sales invoices (expense/purchase rates are excluded). Please choose a sales tax rate.</p>
            {:else if !$formData.xeroUSTaxType}
              <p class="mt-2 text-sm text-yellow-600">⚠️ Without a tax rate selected, invoices with tax will sync incorrectly.</p>
            {/if}
          </div>
        {/if}
      </div>
    {/if}

    {#if $formData.country === 'GB' || $formData.country === 'GI'}
      <div class="border-t pt-6">
        <h4 class="text-lg font-medium text-gray-900 mb-4">VAT Tax Configuration</h4>
        <div class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p class="text-sm text-blue-800">
            📖 <strong>About VAT Tax Types:</strong><br>Select the Xero tax setting that matches your VAT obligations. Gibraltar currently has 0% VAT — select a Zero Rated or No VAT option.<br>
            This tax code will appear on the invoice created in Xero for invoices coming from JobTread that include tax components.<br>
            The tax calculated in JobTread will be the amount applied to the invoice in Xero.<br>
            <a href="/help/settings/configuring-xero-integration" target="_blank" class="text-blue-600 underline hover:text-blue-800">Learn more</a>
          </p>
        </div>
        {#if $loadingTaxRates}
          <div class="flex items-center justify-center py-4">
            <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 mr-2"></div>
            <span class="text-sm text-gray-600">Loading tax rates from Xero...</span>
          </div>
        {:else if $xeroTaxRates.length === 0}
          <div class="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <p class="text-sm text-yellow-800">⚠️ No tax rates found in your Xero account. Please create a tax rate in Xero first, then refresh this page.</p>
          </div>
        {:else}
          <div>
            <label for="xeroGBTaxType" class="block text-sm font-medium text-gray-700 mb-2">VAT Tax Rate</label>
            <select id="xeroGBTaxType" bind:value={$formData.xeroGBTaxType} class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500">
              <option value="">-- None (tax will not be applied) --</option>
              {#if selectedInvoiceTaxType && !invoiceTaxTypeInList}
                <option value={selectedInvoiceTaxType}>{selectedInvoiceTaxType} (not valid for sales — please reselect)</option>
              {/if}
              {#each $xeroTaxRates as rate}
                <option value={rate.taxType}>{rate.name.replace(/GST/g, 'VAT')} ({rate.displayRate}%)</option>
              {/each}
            </select>
            {#if selectedInvoiceTaxType && !invoiceTaxTypeInList}
              <p class="mt-2 text-sm text-amber-600">The saved tax rate cannot be used on sales invoices (expense/purchase rates are excluded). Please choose a sales VAT rate.</p>
            {:else if !$formData.xeroGBTaxType}
              <p class="mt-2 text-sm text-yellow-600">⚠️ Without a tax rate selected, invoices with tax will sync incorrectly.</p>
            {/if}
          </div>
        {/if}
      </div>
    {/if}

    {#if $formData.country === 'CA'}
      <div class="border-t pt-6">
        <h4 class="text-lg font-medium text-gray-900 mb-4">Canadian Sales Tax</h4>

        {#if !caProvince}
          <div class="mb-4 p-3 bg-amber-50 border border-amber-300 rounded-md">
            <p class="text-sm text-amber-800">
              <strong>Choose your province.</strong> Pick "Canada – your province" in the Country list above so Buildzilla can work out GST, PST, HST or QST on your bills. Until then, bills use the single tax rate read off each bill.
            </p>
          </div>
        {:else}
          <div class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p class="text-sm text-blue-800">
              <strong>{PROVINCES[caProvince].name}:</strong>
              {caComponents.map(c => `${c.code} ${c.rate}%`).join(' + ')}.
              {#if caComponents.length > 1}Each tax is worked out on the pre-tax amount (e.g. $1,000 + {caComponents.map(c => `$${(10 * c.rate).toFixed(2)} ${c.code}`).join(' + ')}).{/if}
              Bills are checked against these rates on the review screen, and posted to Xero with the matching purchase tax rate.
            </p>
          </div>

          <div class="mb-6 p-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-700">
            <p class="font-medium text-gray-800 mb-1">Xero purchase tax rate needed for bills</p>
            <p>Bills post to Xero at <strong>{xeroPurchaseRateHint(caComponents)}</strong>. Make sure your Xero org has an active purchase (expense) tax rate at that rate{caComponents.length > 1 ? ', with those components' : ''}.</p>
            {#if provinceHasPst(caProvince)}
              <p class="mt-2 text-xs text-gray-500">
                PST usually can't be claimed back. In Xero (Accounting › Advanced › Tax rates), tick <strong>Non-recoverable</strong> on the PST component of your purchase rate and Xero adds the PST to the expense. Buildzilla prefers that rate when it posts. In JobTread the PST is added to the job cost and shown on the bill as PST, with GST as the bill's tax.
              </p>
            {/if}
          </div>
        {/if}

        <p class="text-sm text-gray-600 mb-3">
          <strong>Sales invoices:</strong> each JobTread invoice is matched to the Xero sales tax rate with the same percentage. The rate below is used when no rate matches.
          <a href="/help/settings/configuring-xero-integration" target="_blank" class="text-blue-600 underline hover:text-blue-800">Learn more</a>
        </p>
        {#if $loadingTaxRates}
          <div class="flex items-center justify-center py-4">
            <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 mr-2"></div>
            <span class="text-sm text-gray-600">Loading tax rates from Xero...</span>
          </div>
        {:else if $xeroTaxRates.length === 0}
          <div class="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <p class="text-sm text-yellow-800">No tax rates found in your Xero account. Please create a tax rate in Xero first, then refresh this page.</p>
          </div>
        {:else}
          <div>
            <label for="xeroCATaxType" class="block text-sm font-medium text-gray-700 mb-2">Default sales tax rate (sales invoices)</label>
            <select id="xeroCATaxType" bind:value={$formData.xeroCATaxType} class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500">
              <option value="">-- None (tax will not be applied) --</option>
              {#if selectedInvoiceTaxType && !invoiceTaxTypeInList}
                <option value={selectedInvoiceTaxType}>{selectedInvoiceTaxType} (not valid for sales — please reselect)</option>
              {/if}
              {#each $xeroTaxRates as rate}
                <option value={rate.taxType}>{rate.name} ({rate.displayRate}%)</option>
              {/each}
            </select>
            {#if selectedInvoiceTaxType && !invoiceTaxTypeInList}
              <p class="mt-2 text-sm text-amber-600">The saved tax rate cannot be used on sales invoices (expense/purchase rates like INPUT are excluded). Please choose a sales GST/HST rate.</p>
            {:else if !$formData.xeroCATaxType}
              <p class="mt-2 text-sm text-yellow-600">Without a tax rate selected, invoices with tax will sync incorrectly.</p>
            {/if}
          </div>
        {/if}
      </div>
    {/if}

    </fieldset>
  </div>
</SettingsLayout>
